import { BrowserRouter, Route, Routes as RouterRoutes, Navigate } from 'react-router-dom';
import React from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import Shield from './components/Shield';
// Pages
import LandingPage from './pages/landing-page';
import SpanishLanguageLandingPage from './pages/spanish-language-landing-page';
import InmateSearchWidget from './pages/inmate-search-widget';
import NotFound from './pages/NotFound';
import AdminLogin from './pages/admin-login';
import AuthenticationLoginPortal from './pages/authentication-login-portal';
import BailAgentManagement from './pages/bail-agent-management';
import DatabaseManagement from './pages/database-management';
import ChatInterface from './pages/chat-interface';
import PaymentPlansPage from './pages/payment-plans-page';
import WorkerVerificationPlaybook from './pages/worker-verification-playbook';
import FiveStepBailGuideWithFAQ from './pages/5-step-bail-guide-with-faq';
import Homepage from './pages/homepage';
import AboutUs from './pages/about-us';
import ContactUs from './pages/contact-us';
import ServicesPage from './pages/services';
import LocationsPage from './pages/locations';
import BlogPage from './pages/blog';
import BlogArticleDetail from './pages/blog-article';
import HomepageRedesign from './pages/homepage-redesign';
import LegalDisclaimer from './pages/legal-disclaimer';
import GearPage from './pages/gear';

import EnhancedBlogSystem from "./pages/enhanced-blog-system";
import CSAPPage from './pages/client-success-accountability-program-csap';

import ToolsPage from './pages/tools';
import EnhancedNavigationSystem from './pages/enhanced-navigation-system';

import PrivacyPolicyPage from './pages/privacy-policy';
import TermsAndConditionsPage from './pages/terms-and-conditions';
import TermsOfServicePage from './pages/terms-of-service';
import LegalDisclaimerAndCompliancePage from './pages/legal-disclaimer-and-compliance';

import SEODashboard from './pages/seo-dashboard-analytics-hub';
import SchemaMarkupGenerator from './pages/schema-markup-generator-implementation';

import LocalSEOOptimizationCenter from './pages/local-seo-optimization-center';

// New Admin Credentials Management page
import AdminCredentialsManagement from './pages/admin-credentials-management';

import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import AppointmentSchedulingSystem from './pages/appointment-scheduling-system/index';
import AppointmentResponse from 'pages/appointment-response';
import AppointmentManagementDashboard from 'pages/appointment-management-dashboard';
import ApproveAppointment from 'pages/approve-appointment';
import DeclineAppointment from 'pages/decline-appointment';
import AgentAvailabilityAdmin from "pages/agent-availability-admin";

// Google Analytics Tracker Component - must be inside BrowserRouter
function GATracker() {
  useGoogleAnalytics();
  return null;
}

export default function ProjectRoutes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Landing Pages - Redesigned Homepage as Main */}
          <Route path="/" element={<HomepageRedesign />} />
          
          {/* About Pages - Redirect /about to /about-us for consistency */}
          <Route path="/about" element={<Navigate to="/about-us" replace />} />
          <Route path="/about-us" element={<AboutUs />} />
          
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/enhanced-navigation-system" element={<EnhancedNavigationSystem />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/locations" element={<LocationsPage />} />
          
          {/* Gear/Merchandise */}
          <Route path="/gear" element={<GearPage />} />
          
          {/* CSAP Program */}
          <Route path="/client-success-accountability-program-csap" element={<CSAPPage />} />
          
          {/* Legacy Routes (Old Versions) - Keeping for backward compatibility */}
          <Route path="/old-homepage" element={<Homepage />} />
          <Route path="/old-landing" element={<LandingPage />} />
          
          {/* Other Landing Pages */}
          <Route path="/spanish-landing" element={<SpanishLanguageLandingPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticleDetail />} />
          
          {/* Main Application Routes */}
          <Route path="/inmate-search-widget" element={<InmateSearchWidget />} />
          
          {/* Admin & Management */}
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin-credentials-management" element={<AdminCredentialsManagement />} />
          <Route path="/agent-availability-admin" element={<AgentAvailabilityAdmin />} />
          <Route path="/authentication-login-portal" element={<AuthenticationLoginPortal />} />
          <Route path="/bail-agent-management" element={<BailAgentManagement />} />
          <Route path="/database-management" element={<DatabaseManagement />} />
          <Route path="/chat-interface" element={<ChatInterface />} />
          
          {/* Services & Information */}
          <Route path="/payment-plans-page" element={<PaymentPlansPage />} />
          <Route 
            path="/client-portal-dashboard" 
            element={
              <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="max-w-2xl mx-auto px-6 text-center">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-12 shadow-2xl">
                    <h1 className="text-3xl font-bold text-white mb-6">
                      Access Your Bail Account
                    </h1>
                    <p className="text-blue-100 text-xl mb-8 leading-relaxed">
                      Click the button below to securely access your bail bond account information, payment history, and case updates.
                    </p>
                    <a 
                      href="https://agents.simplybail.com/m/sailorsbailbonds43/office?code"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-12 py-6 rounded-2xl font-black text-2xl hover:from-yellow-300 hover:to-amber-400 transition-all transform hover:scale-105 shadow-2xl border-4 border-yellow-300 hover:border-yellow-200 flex items-center justify-center mx-auto max-w-md"
                    >
                      <Shield className="w-8 h-8 mr-4 group-hover:animate-pulse" />
                      ACCESS YOUR BAIL ACCOUNT
                    </a>
                    <p className="text-blue-200 text-sm mt-6">
                      Secure portal powered by SimplyBail.com
                    </p>
                  </div>
                </div>
              </div>
            }
          />
          <Route path="/worker-verification-playbook" element={<WorkerVerificationPlaybook />} />
          <Route path="/5-step-bail-guide-with-faq" element={<FiveStepBailGuideWithFAQ />} />
          
          {/* SEO Tools Routes */}
          <Route path="/seo-dashboard-analytics-hub" element={<SEODashboard />} />
          <Route path="/schema-markup-generator-implementation" element={<SchemaMarkupGenerator />} />
          
          {/* Legal Documents Routes */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/legal-disclaimer" element={<LegalDisclaimer />} />
          <Route path="/legal-disclaimer-and-compliance" element={<LegalDisclaimerAndCompliancePage />} />
          
          {/* Appointment Scheduling Routes - SHORT URL: /schedule */}
          <Route path="/schedule" element={<AppointmentSchedulingSystem />} />
          <Route path="/appointment-scheduling-system" element={<AppointmentSchedulingSystem />} />
          <Route path="/appointment-management-dashboard" element={<AppointmentManagementDashboard />} />
          <Route path="/appointment-response" element={<AppointmentResponse />} />
          <Route path="/approve-appointment" element={<ApproveAppointment />} />
          <Route path="/decline-appointment" element={<DeclineAppointment />} />
          
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
          <Route path="/enhanced-blog-system" element={<EnhancedBlogSystem />} />
          <Route path="/local-seo-optimization-center" element={<LocalSEOOptimizationCenter />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}